import {
  type DocumentNode,
  type FieldNode,
  Kind,
  type OperationDefinitionNode,
  parse,
  valueFromASTUntyped,
} from 'graphql';

import type { ToolPart } from './ApprovalActions';
import {
  type MailPresenterLabels,
  presentMailTool,
} from './mailToolPresenters';
import {
  type SandboxPresenterLabels,
  presentSandboxTool,
} from './sandboxToolPresenters';

// Readable view of a tool call for non-developer users: the expanded chat step
// shows these blocks as "Input" / "Result", and keeps the raw query, variables
// and output behind "Technical details". Everything here is derived by rules
// from the tool input/output — never from agent-written text — so a mutation's
// readable view always reflects the payload that actually runs.

export type DetailRow = {
  label: string;
  value: string;
  // Untruncated value, shown on hover when `value` shortens ids.
  full?: string;
  // Alternatives of one `_or`: rendered as a shaded band, every row after the
  // first prefixed with "or", so it reads apart from the ANDed conditions.
  orGroup?: 'first' | 'next';
  // Shows a copy button next to the value (server names, ports).
  copyable?: boolean;
};

export type ReadableBlock =
  | { kind: 'rows'; title?: string; rows: Array<DetailRow> }
  | {
      kind: 'chips';
      title?: string;
      items: Array<{ label: string; hint?: string }>;
      hidden: number;
    }
  | {
      kind: 'links';
      items: Array<{ title: string; href: string; snippet?: string }>;
    }
  | { kind: 'note'; text: string }
  | { kind: 'text'; text: string }
  | {
      kind: 'table';
      title?: string;
      columns: Array<string>;
      rows: Array<Array<string>>;
    }
  // Pass/fail list, e.g. the steps of a mail server probe.
  | {
      kind: 'checks';
      items: Array<{ label: string; ok: boolean; detail: string }>;
    }
  // A shell command, shown terminal style.
  | { kind: 'command'; text: string }
  // Status chips, e.g. a command's exit code and the sandbox network.
  | {
      kind: 'badges';
      items: Array<{
        label: string;
        tone: 'success' | 'error' | 'neutral';
        icon?: 'online' | 'offline';
        hint?: string;
      }>;
    }
  // Long verbatim text (stdout, stderr), collapsed until the user opens it.
  | { kind: 'collapsible'; title: string; text: string };

export type ToolPresentation = {
  input: Array<ReadableBlock>;
  // 'raw': the Result section shows the regular output segments (the JSON
  // returned by GraphQL queries and mutations is readable as is).
  output: Array<ReadableBlock> | 'raw';
  // false when the readable input could not represent the whole call
  // (fragments, directives, several operations): the full detail is then only
  // under "Technical details".
  complete: boolean;
};

// Translated strings, built by the component with literal t() calls.
export type PresenterLabels = {
  action: string;
  record: string;
  allRecords: string;
  andMore: string;
  more: (count: number) => string;
  changes: string;
  create: string;
  update: string;
  delete: string;
  yes: string;
  no: string;
  contains: string;
  notContains: string;
  isEmpty: string;
  isNotEmpty: string;
  oneOf: string;
  noneOf: string;
  or: string;
  lookedAt: string;
  allTables: string;
  availableChanges: string;
  playbook: string;
  playbookLoaded: string;
  search: string;
  language: string;
  page: string;
  opened: string;
  table: string;
  country: string;
  recordId: string;
  filter: string;
  sort: string;
  descending: string;
  fields: string;
  distinct: string;
  limit: string;
  offset: string;
  mail: MailPresenterLabels;
  file: string;
  fileType: string;
  sandbox: SandboxPresenterLabels;
};

// Suffix matches: the GraphQL tools are registered under several namespaces
// (graphql_query, scrapers_query, ... — see createGraphQLTools in crm-chat-api).
export const isQueryTool = (toolName: string): boolean =>
  toolName.endsWith('_query');
export const isMutationTool = (toolName: string): boolean =>
  toolName.endsWith('_mutation');
export const isExploreSchemaTool = (toolName: string): boolean =>
  toolName.endsWith('_explore_schema');

// The typed data tools (crm-chat-api/tools/crm.ts and market-data.ts): one
// table or function per call, a filter in the shape of a Hasura `where`.
export const isTypedCapabilitiesTool = (toolName: string): boolean =>
  /^get_(crm|market_data)_capabilities$/.test(toolName);
export const isTypedQueryTool = (toolName: string): boolean =>
  /^query_(crm|market)_data$/.test(toolName);
export const isTypedGetTool = (toolName: string): boolean =>
  /^get_(crm|market_data)_object$/.test(toolName);
export const isTypedDataTool = (toolName: string): boolean =>
  isTypedCapabilitiesTool(toolName) ||
  isTypedQueryTool(toolName) ||
  isTypedGetTool(toolName);

const MAX_CHIPS = 20;
const MAX_LIST_VALUES = 5;
const MAX_FLATTEN_DEPTH = 3;
const PATH_SEPARATOR = ' › ';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null && !Array.isArray(value);

const str = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

export const humanizeKey = (key: string): string => {
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words.length > 0
    ? `${words.charAt(0).toUpperCase()}${words.slice(1)}`
    : key;
};

const humanizePath = (path: Array<string>): string =>
  path.map(humanizeKey).join(PATH_SEPARATOR);

const compactJson = (value: unknown): string => {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
};

const formatValue = (value: unknown, labels: PresenterLabels): string => {
  if (value == null) {
    return '—';
  }
  if (typeof value === 'string') {
    return value.length > 0 ? value : '""';
  }
  if (typeof value === 'boolean') {
    return value ? labels.yes : labels.no;
  }
  if (typeof value === 'number' || typeof value === 'bigint') {
    return String(value);
  }
  if (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(item => item == null || typeof item !== 'object')
  ) {
    const shown = value
      .slice(0, MAX_LIST_VALUES)
      .map(item => formatValue(item, labels))
      .join(', ');
    return value.length > MAX_LIST_VALUES
      ? `${shown} ${labels.more(value.length - MAX_LIST_VALUES)}`
      : shown;
  }
  return compactJson(value);
};

const parseJson = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const textBlock = (value: unknown): ReadableBlock => ({
  kind: 'text',
  text: typeof value === 'string' ? value : compactJson(value),
});

// Nested objects become one row per leaf ("Address › City"), up to a depth.
const flattenInto = (
  rows: Array<DetailRow>,
  path: Array<string>,
  value: unknown,
  labels: PresenterLabels,
  depth = 0,
): void => {
  if (
    isRecord(value) &&
    Object.keys(value).length > 0 &&
    depth < MAX_FLATTEN_DEPTH
  ) {
    for (const [key, child] of Object.entries(value)) {
      flattenInto(rows, [...path, key], child, labels, depth + 1);
    }
    return;
  }
  rows.push({ label: humanizePath(path), value: formatValue(value, labels) });
};

const COMPARISONS: Record<string, string> = {
  _eq: '=',
  _neq: '≠',
  _gt: '>',
  _gte: '≥',
  _lt: '<',
  _lte: '≤',
};

const likeText = (operand: unknown, labels: PresenterLabels): string =>
  typeof operand === 'string'
    ? `"${operand.replace(/^%|%$/g, '')}"`
    : formatValue(operand, labels);

const conditionText = (
  operator: string,
  operand: unknown,
  labels: PresenterLabels,
): string | null => {
  const comparison = COMPARISONS[operator];
  if (comparison != null) {
    return `${comparison} ${formatValue(operand, labels)}`;
  }
  switch (operator) {
    case '_in':
      return `${labels.oneOf} ${formatValue(operand, labels)}`;
    case '_nin':
      return `${labels.noneOf} ${formatValue(operand, labels)}`;
    case '_like':
    case '_ilike':
      return `${labels.contains} ${likeText(operand, labels)}`;
    case '_nlike':
    case '_nilike':
      return `${labels.notContains} ${likeText(operand, labels)}`;
    case '_is_null':
      return operand === false ? labels.isNotEmpty : labels.isEmpty;
    default:
      return null;
  }
};

const NEGATED_OPERATORS: Record<string, string> = {
  _eq: '_neq',
  _neq: '_eq',
  _gt: '_lte',
  _gte: '_lt',
  _lt: '_gte',
  _lte: '_gt',
  _in: '_nin',
  _nin: '_in',
  _like: '_nlike',
  _ilike: '_nilike',
  _nlike: '_like',
  _nilike: '_ilike',
};

// A bool_exp that is exactly one condition (`{ a: { b: { _eq: 1 } } }`), so
// `_not` can be shown by flipping its operator. null for anything wider:
// not(A and B) is not (not A and not B).
const singleCondition = (
  value: unknown,
  path: Array<string>,
): { path: Array<string>; operator: string; operand: unknown } | null => {
  if (!isRecord(value)) {
    return null;
  }
  const entries = Object.entries(value);
  const [key, child] = entries[0] ?? [];
  if (entries.length !== 1 || key == null) {
    return null;
  }
  if (!key.startsWith('_')) {
    return singleCondition(child, [...path, key]);
  }
  return ['_and', '_or', '_not'].includes(key)
    ? null
    : { path, operator: key, operand: child };
};

const negatedConditionText = (
  operand: unknown,
  labels: PresenterLabels,
): { path: Array<string>; text: string } | null => {
  const leaf = singleCondition(operand, []);
  if (leaf == null) {
    return null;
  }
  const text =
    leaf.operator === '_is_null'
      ? conditionText('_is_null', leaf.operand === false, labels)
      : NEGATED_OPERATORS[leaf.operator] != null
      ? conditionText(
          NEGATED_OPERATORS[leaf.operator] ?? '',
          leaf.operand,
          labels,
        )
      : null;
  return text != null ? { path: leaf.path, text } : null;
};

// Hasura bool_exp → one row per condition ("Created at" / "≥ 2026-01-01").
// Relationships become paths, `_and` is flattened, `_or` becomes a group of
// alternatives and a single-condition `_not` flips its operator. Anything
// else (nested `_or`, wider `_not`, unknown operators) is kept verbatim as
// compact JSON so nothing is dropped.
const whereInto = (
  rows: Array<DetailRow>,
  path: Array<string>,
  value: unknown,
  labels: PresenterLabels,
  rootLabel: string,
): void => {
  const label = path.length > 0 ? humanizePath(path) : rootLabel;
  if (!isRecord(value)) {
    rows.push({ label, value: formatValue(value, labels) });
    return;
  }
  for (const [key, operand] of Object.entries(value)) {
    if (key === '_and' && (Array.isArray(operand) || isRecord(operand))) {
      for (const item of Array.isArray(operand) ? operand : [operand]) {
        whereInto(rows, path, item, labels, rootLabel);
      }
    } else if (
      key === '_or' &&
      orGroupInto(rows, path, operand, labels, rootLabel)
    ) {
      continue;
    } else if (key === '_not' && isRecord(operand)) {
      const negated = negatedConditionText(operand, labels);
      rows.push(
        negated != null
          ? {
              label:
                negated.path.length > 0 || path.length > 0
                  ? humanizePath([...path, ...negated.path])
                  : rootLabel,
              value: negated.text,
            }
          : { label, value: `${key} ${compactJson(operand)}` },
      );
    } else if (key.startsWith('_')) {
      rows.push({
        label,
        value:
          conditionText(key, operand, labels) ??
          `${key} ${compactJson(operand)}`,
      });
    } else {
      whereInto(rows, [...path, key], operand, labels, rootLabel);
    }
  }
};

// One row per `_or` alternative; an alternative with several conditions is
// joined into one row. false (caller keeps the JSON) for a nested `_or`.
const orGroupInto = (
  rows: Array<DetailRow>,
  path: Array<string>,
  operand: unknown,
  labels: PresenterLabels,
  rootLabel: string,
): boolean => {
  const branches = Array.isArray(operand)
    ? operand
    : isRecord(operand)
    ? [operand]
    : [];
  if (branches.length === 0) {
    return false;
  }
  const groupRows: Array<DetailRow> = [];
  for (const branch of branches) {
    const branchRows: Array<DetailRow> = [];
    whereInto(branchRows, path, branch, labels, rootLabel);
    // `{}` matches every row, so an "any of these" list that contains it
    // matches every row too. Handled means: add no condition.
    if (branchRows.length === 0) {
      return true;
    }
    if (branchRows.some(row => row.orGroup != null)) {
      return false;
    }
    const [only] = branchRows;
    groupRows.push(
      branchRows.length === 1 && only != null
        ? only
        : {
            label: path.length > 0 ? humanizePath(path) : rootLabel,
            value: branchRows
              .map(row => `${row.label} ${row.value}`)
              .join(', '),
          },
    );
  }
  if (groupRows.length === 1) {
    rows.push(...groupRows);
  } else {
    groupRows.forEach((row, index) =>
      rows.push({ ...row, orGroup: index === 0 ? 'first' : 'next' }),
    );
  }
  return true;
};

const orderText = (value: unknown): string => {
  const parts: Array<string> = [];
  const walk = (node: unknown, path: Array<string>) => {
    if (Array.isArray(node)) {
      node.forEach(item => walk(item, path));
    } else if (isRecord(node)) {
      for (const [key, child] of Object.entries(node)) {
        walk(child, [...path, key]);
      }
    } else if (typeof node === 'string') {
      const arrow = node.startsWith('desc')
        ? '↓'
        : node.startsWith('asc')
        ? '↑'
        : node;
      parts.push(`${humanizePath(path)} ${arrow}`);
    } else {
      parts.push(`${humanizePath(path)} ${compactJson(node)}`);
    }
  };
  walk(value, []);
  return parts.join(', ');
};

type ParsedOperation = {
  fields: Array<FieldNode>;
  variables: Record<string, unknown>;
  complete: boolean;
};

const parseOperation = (
  source: string | null,
  inputVariables: unknown,
): ParsedOperation | null => {
  if (source == null) {
    return null;
  }
  let document: DocumentNode;
  try {
    document = parse(source);
  } catch {
    return null;
  }
  const operations = document.definitions.filter(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === Kind.OPERATION_DEFINITION,
  );
  const operation = operations[0];
  if (operation == null) {
    return null;
  }
  let complete =
    document.definitions.length === 1 &&
    (operation.directives?.length ?? 0) === 0;
  const fields: Array<FieldNode> = [];
  for (const selection of operation.selectionSet.selections) {
    if (selection.kind !== Kind.FIELD) {
      complete = false;
    } else if (selection.name.value !== '__typename') {
      if ((selection.directives?.length ?? 0) > 0) {
        complete = false;
      }
      fields.push(selection);
    }
  }
  // No plain fields and nothing was skipped: nothing to describe. Skipped
  // selections (fragments) still return, so the caller can say the summary
  // is incomplete instead of describing a partial change as the whole one.
  if (fields.length === 0 && complete) {
    return null;
  }
  const variables: Record<string, unknown> = {};
  for (const definition of operation.variableDefinitions ?? []) {
    if (definition.defaultValue != null) {
      variables[definition.variable.name.value] = valueFromASTUntyped(
        definition.defaultValue,
      );
    }
  }
  if (isRecord(inputVariables)) {
    Object.assign(variables, inputVariables);
  }
  return { fields, variables, complete };
};

const argumentValue = (
  field: FieldNode,
  name: string,
  variables: Record<string, unknown>,
): unknown => {
  const node = field.arguments?.find(arg => arg.name.value === name)?.value;
  if (node == null) {
    return undefined;
  }
  // An unresolved variable stays visible as `$name` instead of "—".
  if (node.kind === Kind.VARIABLE && !(node.name.value in variables)) {
    return `$${node.name.value}`;
  }
  return valueFromASTUntyped(node, variables);
};

const argumentRows = (
  field: FieldNode,
  variables: Record<string, unknown>,
  labels: PresenterLabels,
  names: Array<string> = (field.arguments ?? []).map(arg => arg.name.value),
): Array<DetailRow> => {
  const rows: Array<DetailRow> = [];
  for (const name of names) {
    const value = argumentValue(field, name, variables);
    if (name === 'where') {
      whereInto(rows, [], value, labels, humanizeKey(name));
    } else if (name === 'order_by') {
      rows.push({ label: humanizeKey(name), value: orderText(value) });
    } else {
      flattenInto(rows, [name], value, labels);
    }
  }
  return rows;
};

// "As agent · Leads": the alias tells apart two selections of the same field.
const fieldTitle = (field: FieldNode): string => {
  const name = humanizeKey(field.name.value);
  return field.alias != null
    ? `${humanizeKey(field.alias.value)} · ${name}`
    : name;
};

const UUID_PATTERN =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

// Ids are unreadable at full length and wrap over several lines: keep their
// first 8 characters, the full value stays on hover and in Technical details.
const shortenIds = (blocks: Array<ReadableBlock>): Array<ReadableBlock> =>
  blocks.map(block =>
    block.kind !== 'rows'
      ? block
      : {
          ...block,
          rows: block.rows.map(row => {
            const value = row.value.replace(
              UUID_PATTERN,
              id => `${id.slice(0, 8)}…`,
            );
            return value === row.value
              ? row
              : { ...row, value, full: row.value };
          }),
        },
  );

const presentQuery = (
  input: Record<string, unknown>,
  labels: PresenterLabels,
): ToolPresentation | null => {
  const parsed = parseOperation(str(input.query), input.variables);
  if (parsed == null) {
    return null;
  }
  return {
    input: shortenIds(
      parsed.fields.map(field => ({
        kind: 'rows',
        title: fieldTitle(field),
        rows: argumentRows(field, parsed.variables, labels),
      })),
    ),
    output: 'raw',
    complete: parsed.complete,
  };
};

type MutationAction = 'create' | 'update' | 'delete';

// Hasura names every generated mutation after its table with a fixed prefix
// and suffix, so these patterns cover any table without schema knowledge.
const MUTATION_PATTERNS: Array<[RegExp, MutationAction]> = [
  [/^insert_(\w+?)_one$/, 'create'],
  [/^insert_(\w+)$/, 'create'],
  [/^update_(\w+?)_by_pk$/, 'update'],
  [/^update_(\w+?)_many$/, 'update'],
  [/^update_(\w+)$/, 'update'],
  [/^delete_(\w+?)_by_pk$/, 'delete'],
  [/^delete_(\w+)$/, 'delete'],
];

const matchMutation = (
  fieldName: string,
): { action: MutationAction; table: string } | null => {
  for (const [pattern, action] of MUTATION_PATTERNS) {
    const table = pattern.exec(fieldName)?.[1];
    if (table != null) {
      return { action, table };
    }
  }
  return null;
};

const mutationBlocks = (
  field: FieldNode,
  variables: Record<string, unknown>,
  labels: PresenterLabels,
): Array<ReadableBlock> => {
  const fieldName = field.name.value;
  const match = matchMutation(fieldName);
  if (match == null) {
    // Custom action (e.g. geocode_address): its arguments as rows.
    return [
      {
        kind: 'rows',
        title: fieldTitle(field),
        rows: argumentRows(field, variables, labels),
      },
    ];
  }
  const actionLabel = labels[match.action];
  const recordRows: Array<DetailRow> = [];
  const changeRows: Array<DetailRow> = [];
  const otherNames: Array<string> = [];
  for (const arg of field.arguments ?? []) {
    const name = arg.name.value;
    const value = argumentValue(field, name, variables);
    if (name === 'pk_columns') {
      flattenInto(recordRows, [], value, labels);
    } else if (name === 'where') {
      pushWhere(recordRows, [], value, labels);
    } else if (name === 'updates' && Array.isArray(value)) {
      // update_<table>_many keeps each group's filter inside `updates`, not as
      // a top-level `where`.
      value.forEach((item, index) =>
        pushUpdateGroup(
          recordRows,
          changeRows,
          value.length > 1 ? [`#${index + 1}`] : [],
          item,
          labels,
        ),
      );
    } else if (name === '_set' || name === 'object') {
      flattenInto(changeRows, [], value, labels);
    } else if (name === '_inc' && isRecord(value)) {
      for (const [key, amount] of Object.entries(value)) {
        const sign = typeof amount === 'number' && amount < 0 ? '' : '+';
        changeRows.push({
          label: humanizeKey(key),
          value: `${sign}${formatValue(amount, labels)}`,
        });
      }
    } else if (name === 'objects' && Array.isArray(value)) {
      value.forEach((item, index) =>
        flattenInto(
          changeRows,
          value.length > 1 ? [`#${index + 1}`] : [],
          item,
          labels,
        ),
      );
    } else {
      otherNames.push(name);
    }
  }
  const blocks: Array<ReadableBlock> = [
    {
      kind: 'rows',
      ...(field.alias != null ? { title: humanizeKey(field.alias.value) } : {}),
      rows: [
        {
          label: labels.action,
          value: `${actionLabel} · ${humanizeKey(match.table)}`,
        },
        ...argumentRows(field, variables, labels, otherNames),
      ],
    },
  ];
  if (recordRows.length > 0) {
    blocks.push({ kind: 'rows', title: labels.record, rows: recordRows });
  }
  if (changeRows.length > 0) {
    blocks.push({ kind: 'rows', title: labels.changes, rows: changeRows });
  }
  return blocks;
};

// A `where` that adds no rows (`{}`, `{ _and: [] }`) matches every record.
const whereIsUniversal = (value: unknown, labels: PresenterLabels): boolean => {
  const rows: Array<DetailRow> = [];
  whereInto(rows, [], value, labels, humanizeKey('where'));
  return rows.length === 0;
};

const pushWhere = (
  rows: Array<DetailRow>,
  path: Array<string>,
  value: unknown,
  labels: PresenterLabels,
): void => {
  const before = rows.length;
  whereInto(rows, path, value, labels, humanizeKey('where'));
  if (rows.length === before) {
    rows.push({
      label:
        path.length > 0
          ? humanizePath([...path, 'where'])
          : humanizeKey('where'),
      value: labels.allRecords,
    });
  }
};

// One `updates` entry of update_<table>_many: its where, its changes.
const pushUpdateGroup = (
  recordRows: Array<DetailRow>,
  changeRows: Array<DetailRow>,
  path: Array<string>,
  item: unknown,
  labels: PresenterLabels,
): void => {
  if (!isRecord(item)) {
    flattenInto(recordRows, path, item, labels);
    return;
  }
  if ('where' in item) {
    pushWhere(recordRows, path, item.where, labels);
  }
  if ('_set' in item) {
    flattenInto(changeRows, path, item._set, labels);
  }
  if (isRecord(item._inc)) {
    for (const [key, amount] of Object.entries(item._inc)) {
      const sign = typeof amount === 'number' && amount < 0 ? '' : '+';
      changeRows.push({
        label: humanizePath([...path, key]),
        value: `${sign}${formatValue(amount, labels)}`,
      });
    }
  }
};

const overwritesOnConflict = (
  field: FieldNode,
  variables: Record<string, unknown>,
): boolean => {
  const conflict = argumentValue(field, 'on_conflict', variables);
  if (!isRecord(conflict)) {
    return false;
  }
  const columns = conflict.update_columns;
  return Array.isArray(columns) && columns.length > 0;
};

const whereMatchesAll = (
  field: FieldNode,
  variables: Record<string, unknown>,
  labels: PresenterLabels,
): boolean => {
  const hasWhere =
    field.arguments?.some(arg => arg.name.value === 'where') === true;
  if (
    hasWhere &&
    whereIsUniversal(argumentValue(field, 'where', variables), labels)
  ) {
    return true;
  }
  const updates = argumentValue(field, 'updates', variables);
  return (
    Array.isArray(updates) &&
    updates.some(
      item =>
        isRecord(item) &&
        'where' in item &&
        whereIsUniversal(item.where, labels),
    )
  );
};

// "Delete · Leads" per root field of a mutation, read from the payload that
// runs, never from agent-written text: the approval row shows it next to the
// agent's title. A where-based update or delete that matches every row appends
// " · All records", because the row starts collapsed and the user approves
// without opening it. null when the document does not parse.
export const mutationActionsOf = (
  source: string | null,
  labels: PresenterLabels,
  variables?: unknown,
): Array<string> | null => {
  const parsed = parseOperation(source, variables);
  if (parsed == null) {
    return null;
  }
  const actions = parsed.fields.map(field => {
    const match = matchMutation(field.name.value);
    if (match == null) {
      return fieldTitle(field);
    }
    // An insert with update_columns overwrites rows that already exist.
    const verb =
      match.action === 'create' && overwritesOnConflict(field, parsed.variables)
        ? `${labels.create} / ${labels.update}`
        : labels[match.action];
    const action = `${verb} · ${humanizeKey(match.table)}`;
    return match.action !== 'create' &&
      whereMatchesAll(field, parsed.variables, labels)
      ? `${action} · ${labels.allRecords}`
      : action;
  });
  // The row shows this list while collapsed, so a skipped selection (a
  // fragment) must leave a visible gap instead of a list that looks final.
  if (!parsed.complete) {
    actions.push(labels.andMore);
  }
  return actions;
};

const presentMutation = (
  input: Record<string, unknown>,
  labels: PresenterLabels,
): ToolPresentation | null => {
  const parsed = parseOperation(str(input.mutation), input.variables);
  if (parsed == null) {
    return null;
  }
  return {
    input: shortenIds(
      parsed.fields.flatMap(field =>
        mutationBlocks(field, parsed.variables, labels),
      ),
    ),
    output: 'raw',
    complete: parsed.complete,
  };
};

const capChips = (
  title: string | undefined,
  items: Array<{ label: string; hint?: string }>,
): ReadableBlock => ({
  kind: 'chips',
  title,
  items: items.slice(0, MAX_CHIPS),
  hidden: Math.max(0, items.length - MAX_CHIPS),
});

// Schema exploration output (crm-chat-api/tools/graphql.ts explorePath):
// "## ..." / "### ..." headings followed by "  name: Type" lines, relationship
// fields suffixed with "  (relationship)".
const SCHEMA_HEADING = /^#{2,3}\s+(.+)$/;
const SCHEMA_ITEM = /^\s{2}([A-Za-z_]\w*):\s*(.+?)(\s{2}\(relationship\))?$/;

const schemaOutputBlocks = (output: unknown): Array<ReadableBlock> => {
  if (typeof output !== 'string') {
    return output == null ? [] : [textBlock(output)];
  }
  const sections: Array<{
    title?: string;
    items: Array<{ label: string; hint?: string }>;
  }> = [];
  for (const line of output.split('\n')) {
    const heading = SCHEMA_HEADING.exec(line);
    if (heading != null) {
      sections.push({ title: heading[1], items: [] });
      continue;
    }
    const item = SCHEMA_ITEM.exec(line);
    if (item != null) {
      if (sections.length === 0) {
        sections.push({ items: [] });
      }
      sections[sections.length - 1]?.items.push({
        label: item[1] ?? '',
        hint: item[3] != null ? `${item[2]} (relationship)` : item[2],
      });
    }
  }
  const blocks = sections
    .filter(section => section.items.length > 0)
    .map(section => capChips(section.title, section.items));
  return blocks.length > 0 ? blocks : [textBlock(output)];
};

const presentExploreSchema = (
  input: Record<string, unknown>,
  output: unknown,
  labels: PresenterLabels,
): ToolPresentation => {
  const path = str(input.path);
  const lookedAt =
    path == null
      ? labels.allTables
      : path === 'mutations'
      ? labels.availableChanges
      : path;
  return {
    input: [
      { kind: 'rows', rows: [{ label: labels.lookedAt, value: lookedAt }] },
    ],
    output: schemaOutputBlocks(output),
    complete: true,
  };
};

const presentLoadSkill = (
  input: Record<string, unknown>,
  output: unknown,
  labels: PresenterLabels,
): ToolPresentation => {
  const text = typeof output === 'string' ? output : null;
  const heading = text != null ? /^#\s+(.+)$/m.exec(text)?.[1]?.trim() : null;
  const name = str(input.name);
  const playbook = heading ?? (name != null ? humanizeKey(name) : null);
  const sections =
    text != null
      ? Array.from(text.matchAll(/^##\s+(.+)$/gm), match => ({
          label: (match[1] ?? '').trim(),
        }))
      : [];
  const outputBlocks: Array<ReadableBlock> =
    heading != null
      ? [
          { kind: 'note', text: labels.playbookLoaded },
          ...(sections.length > 0 ? [capChips(undefined, sections)] : []),
        ]
      : output != null
      ? [textBlock(output)]
      : [];
  return {
    input:
      playbook != null
        ? [
            {
              kind: 'rows',
              rows: [{ label: labels.playbook, value: playbook }],
            },
          ]
        : [],
    output: outputBlocks,
    complete: true,
  };
};

// Only web or in-app links: never render a `javascript:` (or other scheme)
// href coming from tool output.
const safeHref = (value: unknown): string | null => {
  const href = str(value);
  return href != null && /^(https?:\/\/|\/)/i.test(href) ? href : null;
};

const docLinks = (
  entries: Array<unknown>,
): Extract<ReadableBlock, { kind: 'links' }>['items'] =>
  entries.flatMap(entry => {
    if (!isRecord(entry)) {
      return [];
    }
    const title = str(entry.title);
    const href = safeHref(entry.url);
    if (title == null || href == null) {
      return [];
    }
    const snippet = str(entry.snippet) ?? str(entry.description) ?? undefined;
    return [{ title, href, snippet }];
  });

const linksOrText = (output: unknown): Array<ReadableBlock> => {
  if (output == null) {
    return [];
  }
  const parsed = parseJson(output);
  const entries = isRecord(parsed)
    ? Array.isArray(parsed.results)
      ? parsed.results
      : [parsed]
    : [];
  const items = docLinks(entries);
  return items.length > 0 ? [{ kind: 'links', items }] : [textBlock(output)];
};

const presentSearchDocs = (
  input: Record<string, unknown>,
  output: unknown,
  labels: PresenterLabels,
): ToolPresentation => {
  const rows: Array<DetailRow> = [];
  const query = str(input.query);
  if (query != null) {
    rows.push({ label: labels.search, value: query });
  }
  const locale = str(input.locale);
  if (locale != null) {
    rows.push({ label: labels.language, value: locale.toUpperCase() });
  }
  return {
    input: rows.length > 0 ? [{ kind: 'rows', rows }] : [],
    output: linksOrText(output),
    complete: true,
  };
};

const pageInput = (
  value: unknown,
  labels: PresenterLabels,
): Array<ReadableBlock> => {
  const page = str(value);
  return page != null
    ? [{ kind: 'rows', rows: [{ label: labels.page, value: page }] }]
    : [];
};

// Rows shared by the typed tools: what was read, and in which country.
const typedTargetRows = (
  input: Record<string, unknown>,
  labels: PresenterLabels,
): Array<DetailRow> => {
  const rows: Array<DetailRow> = [];
  const objectType = str(input.object_type);
  if (objectType != null) {
    rows.push({ label: labels.table, value: objectType });
  }
  const country = str(input.country);
  if (country != null) {
    rows.push({ label: labels.country, value: country.toUpperCase() });
  }
  return rows;
};

// "-created_at" means descending, as query_crm_data reads it.
const sortText = (value: string, labels: PresenterLabels): string =>
  value.startsWith('-') ? `${value.slice(1)} (${labels.descending})` : value;

const presentTypedQuery = (
  input: Record<string, unknown>,
  labels: PresenterLabels,
): ToolPresentation => {
  const rows = typedTargetRows(input, labels);
  if (isRecord(input.args)) {
    flattenInto(rows, [], input.args, labels);
  }
  if (input.filter != null) {
    whereInto(rows, [], parseJson(input.filter), labels, labels.filter);
  }
  const sort = str(input.sort);
  if (sort != null) {
    rows.push({ label: labels.sort, value: sortText(sort, labels) });
  }
  const fields = str(input.fields);
  if (fields != null) {
    rows.push({ label: labels.fields, value: fields });
  }
  const distinct = str(input.distinct);
  if (distinct != null) {
    rows.push({ label: labels.distinct, value: distinct });
  }
  if (typeof input.limit === 'number') {
    rows.push({ label: labels.limit, value: String(input.limit) });
  }
  if (typeof input.offset === 'number' && input.offset > 0) {
    rows.push({ label: labels.offset, value: String(input.offset) });
  }
  return {
    input: shortenIds([{ kind: 'rows', rows }]),
    output: 'raw',
    complete: true,
  };
};

// save_artifact: the file name and type, never the content (a whole HTML
// report); the content stays under "Technical details". A finished save is a
// file card (SavedFileCard), so the readable output only matters for errors.
const presentSaveArtifact = (
  input: Record<string, unknown>,
  output: unknown,
  labels: PresenterLabels,
): ToolPresentation => {
  const rows: Array<DetailRow> = [];
  const name = str(input.name);
  if (name != null) {
    rows.push({ label: labels.file, value: name });
  }
  const mimeType = str(input.mimeType);
  if (mimeType != null) {
    rows.push({ label: labels.fileType, value: mimeType });
  }
  const fromSandboxPath = str(input.fromSandboxPath);
  if (fromSandboxPath != null) {
    rows.push({ label: labels.sandbox.fromSandbox, value: fromSandboxPath });
  }
  const error = isRecord(output) ? str(output.error) : null;
  return {
    input: rows.length > 0 ? [{ kind: 'rows', rows }] : [],
    output: error != null ? [{ kind: 'text', text: error }] : 'raw',
    complete: true,
  };
};

// show_files / show_crm_images: a finished call is a card (ShowFilesCard,
// ShowCrmImagesCard); the step only shows while it runs or when it failed, so
// the readable output is the error.
const presentShowFiles = (output: unknown): ToolPresentation => {
  const error = isRecord(output) ? str(output.error) : null;
  return {
    input: [],
    output: error != null ? [{ kind: 'text', text: error }] : 'raw',
    complete: true,
  };
};

const presentTypedGet = (
  input: Record<string, unknown>,
  labels: PresenterLabels,
): ToolPresentation => {
  const rows = typedTargetRows(input, labels);
  const id = str(input.id);
  if (id != null) {
    rows.push({ label: labels.recordId, value: id });
  }
  const fields = str(input.fields);
  if (fields != null) {
    rows.push({ label: labels.fields, value: fields });
  }
  return {
    input: shortenIds([{ kind: 'rows', rows }]),
    output: 'raw',
    complete: true,
  };
};

const presentTypedCapabilities = (
  input: Record<string, unknown>,
  labels: PresenterLabels,
): ToolPresentation => {
  const rows: Array<DetailRow> = [
    {
      label: labels.lookedAt,
      value: str(input.object_type) ?? labels.allTables,
    },
  ];
  const country = str(input.country);
  if (country != null) {
    rows.push({ label: labels.country, value: country.toUpperCase() });
  }
  return { input: [{ kind: 'rows', rows }], output: 'raw', complete: true };
};

export const presentTool = (
  part: ToolPart,
  labels: PresenterLabels,
): ToolPresentation | null => {
  const { toolName } = part;
  const input = isRecord(part.input) ? part.input : null;
  if (input == null) {
    return null;
  }
  const output = part.state === 'output-available' ? part.output : undefined;
  if (isQueryTool(toolName)) {
    return presentQuery(input, labels);
  }
  if (isMutationTool(toolName)) {
    return presentMutation(input, labels);
  }
  if (isExploreSchemaTool(toolName)) {
    return presentExploreSchema(input, output, labels);
  }
  if (isTypedQueryTool(toolName)) {
    return presentTypedQuery(input, labels);
  }
  if (isTypedGetTool(toolName)) {
    return presentTypedGet(input, labels);
  }
  if (isTypedCapabilitiesTool(toolName)) {
    return presentTypedCapabilities(input, labels);
  }
  switch (toolName) {
    case 'load_skill':
      return presentLoadSkill(input, output, labels);
    case 'search_docs':
      return presentSearchDocs(input, output, labels);
    case 'read_doc':
      return {
        input: pageInput(input.slug, labels),
        output: linksOrText(output),
        complete: true,
      };
    case 'save_artifact':
      return presentSaveArtifact(input, output, labels);
    case 'show_files':
    case 'show_crm_images':
      return presentShowFiles(output);
    case 'navigate_to':
      return {
        input: pageInput(input.path, labels),
        output:
          isRecord(output) && output.ok === true
            ? [{ kind: 'note', text: labels.opened }]
            : 'raw',
        complete: true,
      };
    default:
      return (
        presentMailTool(toolName, input, output, labels.mail) ??
        presentSandboxTool(toolName, input, output, labels.sandbox)
      );
  }
};

// web_search and fetch_url have no readable presenter. The expanded step keeps
// the raw JSON and only uses the Input / Result captions.
export const showsRawInputResult = (toolName: string): boolean =>
  toolName === 'web_search' || toolName === 'fetch_url';

// A write the server refused before approval carries a reason written for the
// model (crm-chat-api's refusedBeforeApproval): an English header, one `- `
// line per thing Hasura would refuse, then instructions to the agent. The user
// is shown the problem lines only, or the whole reason if it has none.
export const refusalProblemsOf = (reason: string): string => {
  const problems = reason
    .split('\n')
    .filter(line => line.startsWith('- '))
    .join('\n');
  return problems.length > 0 ? problems : reason;
};
